import to from "await-to-js";
import { getSkyflowRecord } from "config/skyflow";
import vars from "utils/vars";

/** S3 (or API) URL for the filled case PDF on the note — no Vault fetch required. */
export const getCaseFilledPdfUrlFromNote = (note = {}) => {
	const url = note?.case_filled_pdf_url ?? note?.fillable_pdf?.filledPdf ?? "";
	return String(url).trim();
};

/** Note has a downloadable questionnaire PDF (API URL and/or Vault row). */
export const hasQuestionnairePdfOnNote = (note = {}) =>
	Boolean(getCaseFilledPdfUrlFromNote(note)) || Boolean(String(note?.questionnaire_skyflow_id ?? "").trim());

/** Fields from GET patient_questionnaires (proxy-normalized `records` or flat `data`). */
export const getQuestionnaireCaseFileUrlFromVaultGet = (response = {}) => {
	const col = vars.app.skyflowQuestionnaireFileColumn || "questionnaire_case_file";
	const first = response?.records?.[0];
	if (first?.fields && typeof first.fields === "object") {
		const u = first.fields[col];
		if (u) return String(u);
	}
	const row = Array.isArray(response?.data) ? response.data[0] : response?.data;
	if (row && typeof row === "object" && !Array.isArray(row) && row[col]) return String(row[col]);
	const top = response?.fields?.[col];
	return top ? String(top) : "";
};

const isVaultNotFoundError = (error = {}) => {
	const { status, errors = [] } = error?.response?.data || error || {};
	const statusCodes = [status, ...(errors?.map((e) => e?.error?.code || "") || [])].filter(Boolean);
	return statusCodes.some((item) => [404, 400].includes(item));
};

const runVaultGet = async (payload) => {
	const [getError, res] = await to(getSkyflowRecord(payload));
	if (getError) {
		const errorPayload = getError?.response?.data || getError;
		return {
			response: null,
			requestError: errorPayload,
			isNotFound: isVaultNotFoundError(getError),
		};
	}
	return { response: res, requestError: null, isNotFound: false };
};

const vaultPayload = (filter) => ({
	table: vars.app.skyflowTables.patientQuestionnaire,
	redaction: "PLAIN_TEXT",
	...filter,
});

/**
 * Resolves questionnaire_case_file URL from Skyflow patient_questionnaires.
 *
 * @returns {Promise<{ pdfUrl?: string, requestError?: object, isNotFound?: boolean, flashMessage?: string, flashId?: string }>}
 */
export const fetchQuestionnaireCaseFileUrl = async ({
	noteId,
	questionnaireSkyflowId,
	documentSkyflowId,
	caseFilledPdfUrl,
}) => {
	const directUrl = String(caseFilledPdfUrl ?? "").trim();
	if (directUrl) return { pdfUrl: directUrl };

	const qidStored = String(questionnaireSkyflowId ?? "").trim();
	const docSkyflowId = String(documentSkyflowId ?? "").trim();

	if (docSkyflowId) {
		const { response, requestError, isNotFound } = await runVaultGet(
			vaultPayload({ column_name: "skyflow_id", column_values: [docSkyflowId] })
		);
		if (requestError) return { requestError, isNotFound };

		const pdfUrl = getQuestionnaireCaseFileUrlFromVaultGet(response);
		if (!pdfUrl) {
			return {
				flashMessage: "Questionnaire PDF is not available in Vault yet.",
				flashId: "questionnaire_pdf_missing",
			};
		}
		return { pdfUrl };
	}

	if (qidStored) {
		const { response, requestError, isNotFound } = await runVaultGet(
			vaultPayload({ column_name: "skyflow_id", column_values: [qidStored] })
		);
		if (requestError) return { requestError, isNotFound };

		const pdfUrl = getQuestionnaireCaseFileUrlFromVaultGet(response);
		if (!pdfUrl) {
			return {
				flashMessage: "Questionnaire PDF is not available in Vault yet.",
				flashId: "questionnaire_pdf_missing",
			};
		}
		return { pdfUrl };
	}

	if (noteId) {
		const { response, requestError, isNotFound } = await runVaultGet(
			vaultPayload({ column_name: "note_id", column_values: noteId })
		);
		if (requestError) return { requestError, isNotFound };

		let pdfUrl = getQuestionnaireCaseFileUrlFromVaultGet(response);
		if (!pdfUrl) {
			const fields = response?.records?.[0]?.fields || response?.data?.[0] || {};
			const innerQid = fields?.skyflow_id ?? fields?.skyflowID ?? fields?.id;
			if (innerQid) {
				const retry = await runVaultGet(
					vaultPayload({ column_name: "skyflow_id", column_values: [String(innerQid).trim()] })
				);
				if (retry.requestError) return { requestError: retry.requestError, isNotFound: retry.isNotFound };
				pdfUrl = getQuestionnaireCaseFileUrlFromVaultGet(retry.response);
			}
		}

		if (!pdfUrl) {
			return {
				flashMessage: "Questionnaire PDF is not available in Vault yet.",
				flashId: "questionnaire_pdf_missing",
			};
		}
		return { pdfUrl };
	}

	return {
		flashMessage: "No questionnaire file is linked to this note yet.",
		flashId: "questionnaire_pdf_unavailable",
	};
};

export const isQuestionnaireVaultDocument = (document = {}, noteState = {}) =>
	document?.assessment_type === "questionnaire" ||
	(Boolean(document?.skyflow_id) &&
		Boolean(noteState?.questionnaire_skyflow_id) &&
		String(document.skyflow_id).trim() === String(noteState.questionnaire_skyflow_id).trim());
